<template>
	<Header :title="detailsState.details.value?.name ?? ''">
		<ConfettiButton
			v-if="exported"
			as="a"
			button-text="App Exported"
			@click="activeTab = 'exports'" />
		<Button
			v-else
			icon="fal fa-download"
			label="Export app"
			severity="primary"
			@click="handleExportClick" />
	</Header>
	<div class="details-tabs">
		<Tabs
			v-model="activeTab"
			:items="tabItems"
			@update:model-value="handleTabChange" />
	</div>

	<div class="details-panels">
		<EditApp
			v-if="activeTab === 'edit'"
			:app
			:details-state="detailsState"
			@get-details="getDetails()" />
		<Exports
			v-else-if="activeTab === 'exports'"
			:app
			:building="detailsState.isLoading.value"
			:details-state="detailsState" />
		<Settings
			v-else-if="activeTab === 'settings'"
			:details-state="detailsState"
			@get-details="getDetails()" />
		<Admin
			v-else-if="activeTab === 'admin' && authStore.user?.role === 'admin'"
			:app
			:details-state="detailsState"
			@get-details="getDetails()" />
	</div>
</template>

<script setup lang="ts">
import Header from '@/components/Header.vue'
import EditApp from './components/Details/EditApp.vue'
import Settings from './components/Details/Settings.vue'
import Admin from './components/Details/Admin.vue'
import Exports from './components/Details/Exports.vue'
import Tabs from '@/components/Tabs.vue'
import { useAuthStore } from '@/stores/Auth'
import { useRouter, useRoute } from 'vue-router'
import { useAppDetailsState } from '@/models/App/States'
import { ref, watch, onMounted, computed, type Ref } from 'vue'
import * as Sentry from '@sentry/browser'
import { EnrichedApp } from '@codecannon/schemas/schemas/EnrichedApp'
import { useToast } from 'primevue'
import ConfettiButton from '@/components/ConfettiButton.vue'
import Button from 'primevue/button'
import { useSchemaValidation } from '@/views/App/composables/useSchemaValidation'

const router = useRouter()
const route = useRoute()
const detailsState = useAppDetailsState()
const toast = useToast()
const authStore = useAuthStore()
const exported = computed(() => detailsState?.details.value?.latest_build?.export)
const activeBuild = computed(() => detailsState.details.value?.latest_build)
const activeBuildId = computed(() => activeBuild.value?.id)

const { app } = useApp()
const { handleTabChange, activeTab, tabItems } = useTabs()
const { validateSchema } = useSchemaValidation()

function handleExportClick() {
	if (validateSchema(app.value, route.params.id as string)) {
		router.push(`/apps/${route.params.id}/checkout`)
	}
}

onMounted(() => {
	getDetails()
})

async function getDetails() {
	try {
		await detailsState.getDetails(route.params.id as string, {
			with: [
				'creator',
				'latestBuild.latestDeployment.creator',
				'latestBuild.export',
				'repositoryConfigs',
			],
		})
	} catch (error: any) {
		if (error.response?.status === 404) {
			router.push({ name: 'not-found' })
		} else {
			Sentry.captureException(error)
			throw error
		}
	}
}

function useTabs() {
	const activeTab = ref<'edit' | 'exports' | 'settings' | 'admin'>('edit')
	const tabItems = computed(() => {
		const items = [
			{ label: 'Edit App', value: 'edit' },
			{ label: 'Exports', value: 'exports' },
			{ label: 'Settings', value: 'settings' },
		] as { label: string; value: 'edit' | 'exports' | 'settings' | 'admin' }[]
		if (authStore.user?.role === 'admin') items.push({ label: 'Admin', value: 'admin' })
		return items
	})

	watch(
		() => route.query.tab,
		(q) => {
			const allowed = ['edit', 'exports', 'settings'] as const
			const isAdminTab = q === 'admin' && authStore.user?.role === 'admin'
			const next =
				(allowed as readonly string[]).includes(String(q)) || isAdminTab ? (q as any) : 'edit'
			if (activeTab.value !== next) activeTab.value = next
		},
		{ immediate: true },
	)

	function handleTabChange(val: string) {
		router.replace({ query: { tab: val } })
	}

	return { handleTabChange, activeTab, tabItems }
}

function useApp() {
	const app = ref<EnrichedApp | null>(null) as Ref<EnrichedApp | null>
	const appName = ref<string>('')

	watch(
		() => activeBuildId.value,
		async () => {
			try {
				app.value = activeBuild.value
					? new EnrichedApp({ modules: activeBuild.value!.app_schema })
					: null
			} catch (err) {
				console.error(err)
				Sentry.captureException(err)
				toast.add({
					severity: 'error',
					summary: 'Invalid app schema',
					detail:
						'Our team has been notified of this internal issue and will be adressing it as promptly as possible',
					life: 5000,
				})
			}
		},
	)

	watch(
		() => detailsState.details.value,
		() => {
			appName.value = detailsState.details.value?.name ?? ''
		},
	)

	return {
		app,
		appName,
	}
}
</script>

<style scoped lang="scss">
.details-tabs {
	width: 100%;
}

.details-panels {
	width: 100%;
	overflow: auto;
	display: flex;
	justify-content: center;
	min-height: 2px;

	& > * {
		width: 100%;
	}
}

.details-tab-placeholder {
	padding: 16px;
}
</style>
